// PreventiApp · Sprint 5 · Paso 5 · Explorador Operativo (Nivel 3 del Dashboard)
//
// Reorganización FUNCIONAL del dashboard existente (no un módulo nuevo).
// Pestañas:
//   · Prioridades   — casos que requieren acción hoy (compartido con Nivel 1).
//   · Observaciones — listado filtrable de observaciones recientes.
//   · Casos         — lista completa filtrable (reutiliza CasosList).
//
// No modifica la matriz preventiva, no muestra score crudo, no altera RLS.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ClipboardList, Folder, ArrowRight, Filter } from 'lucide-react';
import CasosList from './CasosList';
import { useAccionHoy } from '../../../lib/dashboardQueries';
import { track } from '../../../lib/metrics';
import { nivelInfo, anonLabel } from '../../../lib/dashboardVocab';

const TABS = [
  { key: 'prioridades',   label: 'Prioridades',   icon: Zap,           testid: 'tab-prioridades' },
  { key: 'observaciones', label: 'Observaciones', icon: ClipboardList, testid: 'tab-observaciones' },
  { key: 'casos',         label: 'Casos',         icon: Folder,        testid: 'tab-casos' },
];

export default function ExploradorOperativo({ initialTab = 'prioridades' }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <section
      className="bg-white rounded-2xl border border-[#E5EAF5] p-4 sm:p-6"
      data-testid="explorador-operativo"
    >
      <div className="mb-4">
        <div
          role="tablist"
          aria-label="Explorador operativo"
          className="inline-flex bg-[#F4F7FE] rounded-full p-1 border border-[#E5EAF5]"
        >
          {TABS.map(({ key, label, icon: Icon, testid }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => {
                setTab(key);
                track('tab_change', { tab: key });
              }}
              data-testid={testid}
              className={`flex items-center gap-2 px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full font-bold transition ${
                tab === key
                  ? 'bg-white text-[#0B1B5E] shadow-sm'
                  : 'text-[#475472] hover:text-[#0B1B5E]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel">
        {tab === 'prioridades'   && <PrioridadesTab />}
        {tab === 'observaciones' && <ObservacionesTab />}
        {tab === 'casos'         && <CasosList />}
      </div>
    </section>
  );
}

// -------------------------------------------------------------------
// Prioridades — casos activos que necesitan atención hoy.
// -------------------------------------------------------------------
function PrioridadesTab() {
  const q = useAccionHoy();
  const casos = q.data?.casos || [];
  return (
    <div className="space-y-2" data-testid="explorador-prioridades">
      <p className="text-xs text-[#475472] mb-2">
        Los casos con próxima revisión vencida, sin primera acción, con evento centinela
        activo o en carril crisis aparecen aquí para intervención hoy.
      </p>
      {q.isLoading && <div className="text-xs text-[#94A3B8]">Cargando…</div>}
      {!q.isLoading && casos.length === 0 && (
        <p className="text-xs text-[#94A3B8] italic" data-testid="prioridades-vacio">
          No hay casos que requieran acción inmediata. Excelente.
        </p>
      )}
      <ul className="divide-y divide-[#F1F3F9]" data-testid="prioridades-lista">
        {casos.map((c) => {
          const n = nivelInfo(c.nivel_alerta);
          return (
            <li key={c.id} className="py-2.5 flex items-center gap-3">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: n.color }}
                aria-label={n.label}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#0B1B5E] truncate">
                  {anonLabel(c.estudiante_id, c.estudiante_codigo)}
                </p>
                <p className="text-[11px] text-[#475472] truncate">
                  {c.dominio_code} · {(c.motivos_prioridad || []).join(' · ') || 'Sin motivos'}
                </p>
              </div>
              <Link
                to={`/admin/dashboard/casos/${c.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#5346E6] hover:underline shrink-0"
                data-testid={`prioridad-abrir-${c.id.slice(0, 8)}`}
                onClick={() => track('accion_hoy_open', { nivel: c.nivel_alerta || 'na' })}
              >
                Abrir <ArrowRight className="w-3 h-3" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -------------------------------------------------------------------
// Observaciones — atajo al listado filtrable en /admin/observaciones.
// Mantener aquí un CTA claro evita duplicar código.
// -------------------------------------------------------------------
function ObservacionesTab() {
  return (
    <div className="text-sm text-[#475472] space-y-3" data-testid="explorador-observaciones">
      <p>
        Consulta cada observación registrada por docentes, inspectores y demás usuarios
        con su estado, dominio, nivel y evidencias vinculadas.
      </p>
      <div className="rounded-xl border border-[#E5EAF5] bg-[#F4F7FE] p-4 flex items-start gap-3">
        <Filter className="w-4 h-4 text-[#5346E6] mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-[#0B1B5E] font-bold mb-1">Filtros disponibles</p>
          <p className="text-xs text-[#475472]">
            Fecha, estado (registrado, en seguimiento, cerrado), modo (operativo / inducción)
            y dominio (D1…D6). Tu rol determina qué observaciones puedes ver.
          </p>
        </div>
        <Link
          to="/admin/observaciones"
          className="inline-flex items-center gap-1 rounded-full bg-[#5346E6] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#4237c2] transition shrink-0"
          data-testid="cta-ir-observaciones"
        >
          Ir al listado <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
